import { useState, useEffect } from "react";

import { AlertTriangle, Trash2, Wrench, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";


interface OrphanedBoard {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  workspace_id: string;
  issue: "no_projects" | "no_sessions";
  project_id?: string;
}

export const BoardRecoveryTool = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orphanedBoards, setOrphanedBoards] = useState<OrphanedBoard[]>([]);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      detectOrphanedBoards();
    }
  }, [user]);

  const detectOrphanedBoards = async () => {
    setLoading(true);
    try {
      // Get user's workspace
      const { data: workspace } = await supabase
        .from("workspaces")
        .select("id")
        .eq("user_id", user?.id)
        .single();

      if (!workspace) {
        setLoading(false);
        return;
      }

      // Get all boards
      const { data: boards, error: boardsError } = await supabase
        .from("boards")
        .select("*")
        .eq("workspace_id", workspace.id);

      if (boardsError) throw boardsError;

      const orphaned: OrphanedBoard[] = [];

      // Check each board for issues
      for (const board of boards || []) {
        // Check if board has projects
        const { data: projects } = await supabase
          .from("projects")
          .select("id")
          .eq("board_id", board.id);

        if (!projects || projects.length === 0) {
          orphaned.push({
            ...board,
            issue: "no_projects"
          });
          continue;
        }

        // Check if any project has sessions
        const { data: sessions } = await supabase
          .from("sessions")
          .select("id")
          .in("project_id", projects.map(p => p.id));

        if (!sessions || sessions.length === 0) {
          orphaned.push({
            ...board,
            issue: "no_sessions",
            project_id: projects[0].id
          });
        }
      }

      setOrphanedBoards(orphaned);
    } catch (error) {
      console.error("Error detecting orphaned boards:", error);
      toast.error("Failed to check for incomplete boards");
    } finally {
      setLoading(false);
    }
  };

  const handleFix = async (board: OrphanedBoard) => {
    setFixing(board.id);
    try {
      if (board.issue === "no_projects") {
        // Create project and session
        const { data: newProject, error: projectError } = await supabase
          .from("projects")
          .insert({
            board_id: board.id,
            name: "My First Project"
          })
          .select()
          .single();

        if (projectError) throw projectError;

        const { data: newSession, error: sessionError } = await supabase
          .from("sessions")
          .insert({
            project_id: newProject.id,
            name: "Session 1"
          })
          .select()
          .single();

        if (sessionError) throw sessionError;

        toast.success("Board setup completed!");
        navigate(`/sessions/${newSession.id}`);
      } else if (board.issue === "no_sessions" && board.project_id) {
        // Create session for existing project
        const { data: newSession, error: sessionError } = await supabase
          .from("sessions")
          .insert({
            project_id: board.project_id,
            name: "Session 1"
          })
          .select()
          .single();

        if (sessionError) throw sessionError;

        toast.success("Board setup completed!");
        navigate(`/sessions/${newSession.id}`);
      }

      // Refresh the list
      await detectOrphanedBoards();
    } catch (error) {
      console.error("Error fixing board:", error);
      toast.error("Failed to complete board setup");
    } finally {
      setFixing(null);
    }
  };

  const handleDelete = async (board: OrphanedBoard) => {
    if (!confirm(`Are you sure you want to delete "${board.name}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting(board.id);
    try {
      // Delete the board (cascading will handle related records)
      const { error } = await supabase
        .from("boards")
        .delete()
        .eq("id", board.id);

      if (error) throw error;

      toast.success("Board deleted successfully");
      await detectOrphanedBoards();
    } catch (error) {
      console.error("Error deleting board:", error);
      toast.error("Failed to delete board");
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (orphanedBoards.length === 0) {
    return null;
  }

  return (
    <Alert className="border-warning">
      <AlertTriangle className="h-4 w-4 text-warning" />
      <AlertDescription>
        <div className="space-y-4">
          <div>
            <p className="font-semibold mb-2">
              {orphanedBoards.length} incomplete board{orphanedBoards.length > 1 ? "s" : ""} found
            </p>
            <p className="text-sm text-muted-foreground">
              These boards were created but the setup wasn't completed. You can finish setting them up or remove them.
            </p>
          </div>

          <div className="space-y-2">
            {orphanedBoards.map((board) => (
              <Card key={board.id}>
                <CardHeader className="py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <CardTitle className="text-base">{board.name}</CardTitle>
                      <CardDescription className="text-xs">
                        {board.issue === "no_projects" 
                          ? "Missing projects and sessions"
                          : "Missing sessions"}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => handleFix(board)}
                        disabled={fixing === board.id || deleting === board.id}
                      >
                        {fixing === board.id ? (
                          <>
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            Fixing...
                          </>
                        ) : (
                          <>
                            <Wrench className="h-3 w-3 mr-1" />
                            Complete Setup
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(board)}
                        disabled={fixing === board.id || deleting === board.id}
                      >
                        {deleting === board.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="h-3 w-3 text-destructive" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
};
